import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

interface DeletionRequest {
  id: string;
  status: "pending" | "processing" | "completed" | "denied";
  createdAt: string;
  updatedAt: string;
}

/**
 * The dashed line at the foot of Settings: a person asks for their own account and its
 * data to be deleted, and sees where that request stands. Formerly the Data &
 * Privacy chapter, which held nothing else.
 */
export function DeleteAccountSection() {
  const [request, setRequest] = useState<DeletionRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/account/deletion-status", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRequest(data.request);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/account/request-deletion", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setError(data?.error || "Something went wrong. Please try again.");
        return;
      }
      const data = await res.json();
      setRequest(data.request);
      setShowConfirm(false);
      setReason("");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const statusLabels: Record<string, { label: string; icon: ReactNode; color: string }> = {
    pending: {
      label: "Pending review",
      icon: <Clock size={16} />,
      color: "var(--warning)",
    },
    processing: {
      label: "Being processed",
      icon: <Loader2 size={16} className="animate-spin" />,
      color: "var(--info)",
    },
    completed: {
      label: "Completed",
      icon: <CheckCircle2 size={16} />,
      color: "var(--success)",
    },
    denied: {
      label: "Denied",
      icon: <AlertTriangle size={16} />,
      color: "var(--text-secondary)",
    },
  };

  const pendingRequest = request && (request.status === "pending" || request.status === "processing") ? request : null;

  return (
    <div className="dashed" data-testid="delete-account-section" style={{ padding: "12px 14px", fontSize: 12.5, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <span>
          <b style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Your account.</b> Request permanent deletion of
          your account and all associated data, processed within 30 days: workspace data, conversations, connected
          service tokens, and any data obtained through third-party integrations (including Google).
        </span>
        {loading ? (
          <span className="fact" style={{ fontSize: 11.5 }}>
            Reading
          </span>
        ) : pendingRequest ? (
          <span className="fact" style={{ fontSize: 11.5 }}>
            <span style={{ color: statusLabels[pendingRequest.status]?.color, verticalAlign: "-3px", marginRight: 6 }}>
              {statusLabels[pendingRequest.status]?.icon}
            </span>
            Deletion request submitted on {new Date(pendingRequest.createdAt).toLocaleDateString()}. Status:{" "}
            {statusLabels[pendingRequest.status]?.label}.
          </span>
        ) : !showConfirm ? (
          <button type="button" className="btn quiet sm" style={{ color: "var(--incident)" }} onClick={() => setShowConfirm(true)}>
            Delete my account
          </button>
        ) : null}
      </div>
      {!loading && !pendingRequest && showConfirm && (
        <div className="field top" style={{ color: "var(--text-primary)" }}>
          <label htmlFor="delete-account-reason">Reason</label>
          <textarea
            id="delete-account-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional. Let us know why you're leaving."
            rows={2}
          />
          <div className="help">This cannot be undone. Your data is permanently deleted within 30 days of approval.</div>
          {error && (
            <div className="help" role="alert" style={{ color: "var(--incident)" }}>
              {error}
            </div>
          )}
          <div className="help" style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
            <button
              type="button"
              className="btn quiet sm"
              onClick={() => {
                setShowConfirm(false);
                setReason("");
                setError(null);
              }}
            >
              Cancel
            </button>
            <button type="button" className="btn primary sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting" : "Confirm deletion request"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
